"use client";

import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import type {
  ContentfulHeadlineBlock,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import { cva, type VariantProps } from "class-variance-authority";
import { TextD2C } from "../../elements/text-d2c/text";
import { Column, Container, Row } from "../../layout/grid";
import { deriveD2CTextColors } from "../../lib/d2c-colors";
import { cn } from "../../lib/utils";

const textIntroSectionVariants = cva("", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
    textVariant: {
      "p-xlarge": "",
      "p-large": "",
      p: "",
      "p-small": "",
    },
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
    color: {
      black: "text-black",
      white: "text-white",
      alabastergrey: "text-alabastergrey",
    },
  },
  defaultVariants: {
    columnWidth: 12,
    textVariant: "p",
    backgroundColor: "white",
    color: "black",
  },
});

export interface TextIntroSectionProps
  extends VariantProps<typeof textIntroSectionVariants> {
  sys?: { id: string };
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  backgroundColor?: "black" | "white" | "whitesmoke";
  headlineColor?: "black" | "white" | "alabastergrey";
  color?: "black" | "white" | "alabastergrey";
  headline?: ContentfulHeadlineBlock;
  headlineIntro?: ContentfulHeadlineBlock;
  textIntro?: ContentfulTextBlock;
  headlineLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  headlineVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  anchorId?: string;
  outerContainerOnly?: boolean;
}

export function TextIntroSection({
  columnWidth,
  textVariant = "p",
  headline,
  headlineLevel = "h1",
  headlineVariant = "h1",
  headlineIntro,
  textIntro,
  backgroundColor,
  outerContainerOnly,
  ...props
}: Readonly<TextIntroSectionProps>) {
  const textDocument = textIntro?.text?.json;
  const resolvedTextVariant = textVariant ?? "p";

  const { headlineColor, textColor } = deriveD2CTextColors(backgroundColor);

  const containerClasses = textIntroSectionVariants({
    columnWidth: null,
    backgroundColor,
    color: textColor,
  });

  const columnClasses = textIntroSectionVariants({
    columnWidth,
    backgroundColor: null,
    color: null,
  });

  return (
    <Container
      className={cn(containerClasses, "component-intro-text-section -mb-px")}
      fullWidth={true}
      id={props.anchorId}
      outerContainerOnly={outerContainerOnly}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
    >
      <Row
        className={cn(
          "gap-0",
          outerContainerOnly && "px-3 md:px-6 lg:px-8 xl:px-11.5"
        )}
      >
        <Column className={cn(columnClasses)}>
          <Row>
            <Column className="col-span-12 md:col-span-6">
              {headline && (
                <TextD2C
                  color={headlineColor}
                  tagName={headlineLevel}
                  variant={headlineVariant}
                >
                  <RichTextRenderer richText={headline.headline?.json} />
                </TextD2C>
              )}
            </Column>

            <Column className="col-span-12 mt-8 md:col-span-6 md:mt-0">
              {headlineIntro && (
                <TextD2C color={headlineColor} dash variant="h6">
                  <RichTextRenderer richText={headlineIntro.headline?.json} />
                </TextD2C>
              )}
              {textDocument && (
                <TextD2C color={textColor} variant={resolvedTextVariant}>
                  <RichTextRenderer richText={textDocument} />
                </TextD2C>
              )}
            </Column>
          </Row>
        </Column>
      </Row>
    </Container>
  );
}
